import { Table } from "components";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { RoomTypeCatalogItem, RoomTypeGroup, RoomTypeParentGroup } from "../model/types";

import { getRoomGroupTableHeaders, getRoomParentTableHeaders } from "../lib/roomGroupTableHeaders";

type RoomGroupTableProps = {
  catalog: RoomTypeCatalogItem[];
  groups: RoomTypeParentGroup[];
  isLoading: boolean;
  onChangeBlur: (group: RoomTypeGroup, fieldName: string, value: string) => void;
  onConfigure: (group: RoomTypeGroup) => void;
  onDelete: (group: RoomTypeGroup) => void;
  onParentChangeBlur: (group: RoomTypeParentGroup, fieldName: string, value: string) => void;
  onParentConfigure: (group: RoomTypeParentGroup) => void;
  onParentDelete: (group: RoomTypeParentGroup) => void;
};

export const RoomGroupTable = ({
  catalog,
  groups,
  isLoading,
  onChangeBlur,
  onConfigure,
  onDelete,
  onParentChangeBlur,
  onParentConfigure,
  onParentDelete,
}: RoomGroupTableProps) => {
  const { t } = useTranslation();

  const childHeaders = useMemo(
    () =>
      getRoomGroupTableHeaders({ catalog, onConfigure, t }).filter(
        (header) => !["color", "config"].includes(header.dataKey)
      ),
    [catalog, onConfigure, t]
  );
  const parentHeaders = useMemo(
    () => getRoomParentTableHeaders({ catalog, onConfigure: onParentConfigure, t }),
    [catalog, onParentConfigure, t]
  );

  const handleBlur = ({ fieldName, row, value }: { fieldName: string; row: RoomTypeGroup; value: string }) => {
    onChangeBlur(row, fieldName, value);
  };

  return (
    <LoadingOrEmptyWrapper showEmpty={groups.length === 0} showLoading={isLoading}>
      <Table
        childConfirmDelete={true}
        childEditable={() => true}
        childHeaders={childHeaders}
        childIdColumnName="roomFunctionValue"
        childOnBlur={({ fieldName, row, value }: { fieldName: string; row: RoomTypeGroup; value: string }) =>
          handleBlur({ fieldName, row, value })
        }
        childOnDelete={({ row }: { row: RoomTypeGroup }) => onDelete(row)}
        childRowRemoveable={(row: RoomTypeGroup) => row.hasConfig}
        childTableDataKey="children"
        confirmDelete={false}
        data={groups}
        editable={() => true}
        headers={parentHeaders}
        idColumnName="groupId"
        onBlur={({ fieldName, row, value }: { fieldName: string; row: RoomTypeParentGroup; value: string }) =>
          onParentChangeBlur(row, fieldName, value)
        }
        onDelete={({ row }: { row: RoomTypeParentGroup }) => onParentDelete(row)}
        rowRemoveable={(row: RoomTypeParentGroup) => row.children.some((child) => child.hasConfig)}
      />
    </LoadingOrEmptyWrapper>
  );
};
